/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

// NOTE: This entity selector does not support Tabular Data at this time.
class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	getSelectInputHTMLID(pInputHTMLID)
	{
		return `#ENTITY-DISPLAY-MULTIPLE-FOR-${pInputHTMLID}`;
	}

	getTabularSelectInputID(pInputHTMLID, pRowIndex)
	{
		return `#ENTITY-DISPLAY-MULTIPLE-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularSelectDropdownID(pInputHTMLID, pRowIndex)
	{
		return `#ENTITY-DISPLAY-MULTIPLE-TABULAR-DROPDOWN-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean}
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, null, pInput, pValue, pHTMLSelector, pRowIndex).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * @param {Record<string, any>} pInput
	 */
	_getEntity(pInput)
	{
		return (pInput?.PictForm?.Entity) || this.options.Entity || null;
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} [pRowIndex] - The row index parameter.
	 * @return {Promise<boolean>}
	 */
	async _onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		// Tabular Print/Read renders the static `...TABULAR-DATA-...` div, not a `...DROPDOWN-...` select2 node.
		const inputMethod = pRowIndex != null ? 'getTabularSelectInputID' : 'getSelectInputHTMLID';
		// Try to get the input element before we do anything else
		const tmpInputSelectElements = this.pict.ContentAssignment.getElement(this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex));
		if (!tmpInputSelectElements || tmpInputSelectElements.length < 1)
		{
			return false;
		}
		/** @type {HTMLSelectElement} */
		const tmpInputSelectElement = tmpInputSelectElements[0];
		// if we have a value (ID), go fetch the Entity for display (Name, etc.)
		if (pValue)
		{
			if (typeof pValue === 'string')
			{
				pValue = pValue.split(',').map((pVal) => pVal.trim());
			}
			if (Array.isArray(pValue) && pValue.length > 0)
			{
				const tmpDisplayValues = [];
				for (const tmpValue of pValue)
				{
					const { Record: tmpEntity, DisplayName: tmpDisplayName } = await this.pict.providers.Select2Util.getEntityWithDisplayAsync(this._getEntity(pInput), tmpValue,
					{
						IdentityField: pInput.PictForm.IdentityField || null,
						JoinEntity: pInput.PictForm.JoinEntity,
						JoinEntityFirst: pInput.PictForm.JoinEntityFirst ?? true,
						EntityDisplayField: pInput.PictForm.EntityDisplayField,
						JoinEntityDisplayField: pInput.PictForm.JoinEntityDisplayField,
						JoinField: pInput.PictForm.JoinField ? pInput.PictForm.JoinField : pInput.PictForm.JoinEntity ? `ID${ pInput.PictForm.JoinEntity }` : null,
						EntityTagLast: pInput.PictForm.EntityTag && pInput.PictForm.EntityTagLast,
					});
					// if we have an EntityTag, display that as a tag along with the Name
					if (pInput.PictForm.EntityTag)
					{
						let tmpDisplayTemplate = '';
						if (tmpInputSelectElement.tagName === 'DIV')
						{
							const tmpEntityTag = tmpEntity[pInput.PictForm.EntityTag];
							const tmpTaggedName = tmpDisplayName || (`Unnamed ${ this._getEntity(pInput) } #` + tmpEntity[`ID${ this._getEntity(pInput) }`]);
							// A configured-but-empty tag paints no badge (per record, so a mixed list still
							// badges the records that have one), rendering as the untagged case below does.
							// This used to invent an "N/A" pill the edit selector never showed. 0 still badges.
							tmpDisplayTemplate = (tmpEntityTag === undefined || tmpEntityTag === null || tmpEntityTag === '') ?
								tmpTaggedName :
								this.pict.providers.Select2Util.buildSelect2CompoundDisplayTemplate(
									tmpEntityTag,
									tmpTaggedName,
									true,
									tmpEntity.EntityTagLast,
								);
						}
						else
						{
							tmpDisplayTemplate = tmpValue || (`Unnamed ${ this._getEntity(pInput) } #` + tmpEntity[`ID${ this._getEntity(pInput) }`]);
						}
						tmpDisplayValues.push(tmpDisplayTemplate);
					}
					else
					{
						tmpDisplayValues.push(tmpDisplayName);
					}
				}
				this.pict.ContentAssignment.assignContent(tmpInputSelectElement, tmpDisplayValues.join(', '));
				return;
			}
		}
		else
		{
			this.pict.ContentAssignment.assignContent(tmpInputSelectElement, '');
		}
	}
}

module.exports = CustomInputHandler;
